import type { QuestDef } from '../../game/quests';
import {
  arrive,
  clearDungeon,
  flag,
  interact,
  items,
  kill,
  main,
  mainRewards,
  reach,
  step,
  talk,
  world,
} from './kit';

/**
 * THE MAIN STORY — "The Aetherfall".
 *
 * Prologue (Lv 1–2, Emberwild Outpost): the player wakes with a sliver of
 * shard-light fused into their palm and proves to be a Shardbearer.
 * Act I (Lv 3–11, Emberwild): grublings and bandits chase shard-light; the
 * Grubling Chieftain has fused the Ember Shard into his club in the
 * Hollowroot Crypt. Interlude (Lv 10–12, Havenreach): High Warden Aldara, the
 * Lantern Watch, the city's services; Elowen hears the second shard in
 * Sunscar. Act II (Lv 12–21, Sunscar + Haven Isle): vanished caravans, the
 * Sandsworn dead, Dunemaw, the Sandbound Warden and the Dune Shard; a ship and
 * a hearth on Haven Isle. Act III (Lv 21–29, Frosthollow): the Hollow Bell,
 * the drowned chapel, the Hollow Legion, Rimefang Alpha and the Frost
 * Matriarch with the Rime Shard; Vhal'Zeroth speaks. Epilogue (Lv 29–30):
 * the Lantern's first flame and the call to the endgame.
 *
 * Voices: Brenna is clipped and dry; Lyra quick and warm; Odo a grumbling
 * merchant; Aldara formal and weary; Borin terse; Elowen an excited scholar;
 * Hadi a worried caravaneer ("friend"); Zahra a cryptic seer ("ember-child");
 * Sol a cheerful sailor; Pim a flustered steward; Vira grave and devout;
 * Bjorn a hunter of very few words.
 *
 * Each quest's turn-in NPC offers the next one, so the conversation flows
 * from reward to the next story beat without leaving the dialogue.
 */
export const MAIN_QUESTS: QuestDef[] = [
  // ================================================================ PROLOGUE — Emberwild Outpost
  main({
    id: 'mq_001',
    name: 'Ember-Touched',
    level: 1,
    zone: 'emberwild',
    giver: 'captain_brenna',
    turnIn: 'captain_brenna',
    auto: true,
    steps: [step(undefined, talk('captain_brenna', 'Speak with Captain Brenna'))],
    offer: [
      'You wake on a cot that smells of woodsmoke and horse. Your right hand is bandaged, and beneath the linen, something glows.',
      'Voices outside the tent. Someone is waiting for you to stand up.',
    ],
    complete: [
      "Easy there. You've been out for two days. Lyra found you in a scorched hollow at the edge of the vale, still smoking.",
      'Three nights ago the sky over the isles cracked like a dropped lamp. Then the stars started falling. Folk are calling it the Aetherfall.',
      'You had one of those falling stars in your fist. A sliver of light, fused into your palm. Anyone else would be ash.',
      "I'm Brenna. I keep this outpost standing. I don't know what you are yet, but if you can hold a blade, the vale needs one.",
    ],
    rewards: mainRewards(1, 0.6, { items: items(['potion_small', 2]) }),
  }),
  main({
    id: 'mq_002',
    name: 'Steel Before Sunrise',
    level: 1,
    zone: 'emberwild',
    giver: 'captain_brenna',
    turnIn: 'captain_brenna',
    requires: ['mq_001'],
    steps: [
      step(
        undefined,
        kill('vale_wolf', 4, 'Slay Vale Wolves north-west of camp', 'emberwild', -40, 66),
      ),
    ],
    offer: [
      'Before I trust you with anything, I want to see you move. The vale wolves north-west of camp have gone rabid since the Aetherfall.',
      'Your skills answer to Q, W, E and R, with more below them. Space throws you clear of trouble. Use all of it.',
      'Thin the pack by four and come back. Then tell me how that hand feels.',
    ],
    progress:
      'The wolves den north-west of camp, past the tents. Drink a draught if they bite deep.',
    complete: [
      'Not bad. Sloppy on the turn, but you hit like you mean it.',
      "And the hand? It flared every time you struck. Huh. I'll pretend I didn't see that, for now.",
    ],
    rewards: mainRewards(1, 1.3),
  }),
  main({
    id: 'mq_003',
    name: 'A Star Falls West',
    level: 2,
    zone: 'emberwild',
    giver: 'captain_brenna',
    turnIn: 'scout_lyra',
    requires: ['mq_002'],
    steps: [
      step(
        undefined,
        reach('emberwild', -50, 56, 9, 'Find the smoking crater in the western meadow'),
      ),
      step(
        'The light in the grass pulses in time with your hand. Something growls behind you.',
        kill('vale_wolf', 5, 'Drive off the wolves drawn to the light', 'emberwild', -56, 44),
      ),
    ],
    offer: [
      "Lyra's been pacing since dawn. She swears another light came down in the western meadow last night.",
      'If that is another piece of whatever hit you, I want it in our hands before the beasts, or worse, find it.',
      'Go. Find the crater, then report to Lyra, not me. She reads the ground better than anyone in this camp.',
    ],
    progress:
      'The crater is in the western meadow, between the wolf dens. Lyra is waiting to hear what you find.',
    complete: [
      'You picked it up. Bare-handed! I watched a wolf try to bite that thing and yelp like it had swallowed a coal.',
      "And now it's just... gone quiet in your palm. Like it went home.",
      "Sky and stone. Brenna needs to hear this from me, before she decides you're some trick of the rifts.",
    ],
    rewards: mainRewards(2, 0.7, { items: items(['potion_small', 2]) }),
  }),
  main({
    id: 'mq_004',
    name: 'Light That Does Not Burn',
    level: 2,
    zone: 'emberwild',
    giver: 'scout_lyra',
    turnIn: 'captain_brenna',
    requires: ['mq_003'],
    steps: [
      step(undefined, talk('captain_brenna', 'Tell Captain Brenna what happened at the crater')),
    ],
    offer: [
      "Come on. Brenna's by the fire, pretending she isn't worried about you.",
      'Let me do the talking first. She believes me about tracks and weather. Glowing strangers, maybe less.',
    ],
    complete: [
      'A shard that settles instead of burning. My grandmother had a word for people like that: Shardbearers. I thought it was a bedtime story.',
      'The Lantern Watch in Havenreach kept the Aether Lantern lit for three hundred years. Its light held the Hollow shut beneath the isles.',
      'The Aetherfall broke the Lantern into seven shards. Wherever they landed, the rifts are waking. And you can hold one.',
      'High Warden Aldara will want you. But the north road is not safe, and the vale is bleeding. Help me here first.',
    ],
    rewards: mainRewards(2, 0.5, { bond: 20 }),
  }),

  // ================================================================ ACT I — Emberwild Vale
  main({
    id: 'mq_005',
    name: 'Thornbacks on the Supply Road',
    level: 3,
    zone: 'emberwild',
    giver: 'captain_brenna',
    turnIn: 'quartermaster_odo',
    requires: ['mq_004'],
    steps: [
      step(
        undefined,
        kill('thornback_boar', 5, 'Slay Thornback Boars on the east road', 'emberwild', 50, 52),
      ),
    ],
    offer: [
      "Odo's supply cart hasn't reached us in two days. Thornback boars are tearing up the east road, and they've turned mean since the sky broke.",
      'Clear them off the road. Odo will want to thank you himself. Try not to let him sell you anything while he does it.',
    ],
    progress: 'The boars root along the east road, beyond the tents. Odo is at his stall.',
    complete: [
      "My road! Well, the road to my cart. The cart itself is in a ditch somewhere, but that's a problem for tomorrow's Odo.",
      "Here: draughts and bandages. On the house. Mostly. Don't tell the captain it was on the house.",
    ],
    rewards: mainRewards(3, 0.6, { items: items(['potion_small', 3]) }),
  }),
  main({
    id: 'mq_006',
    name: 'Tracks Across the River',
    level: 4,
    zone: 'emberwild',
    giver: 'quartermaster_odo',
    turnIn: 'scout_lyra',
    requires: ['mq_005'],
    steps: [
      step(
        undefined,
        reach('emberwild', -44, -18, 12, 'Follow the grubling tracks over the ford to the warren'),
      ),
      step(
        'Torn crates litter the warren. Grublings root through them, their eyes shining with a strange light.',
        kill('grubling', 8, 'Slay Grublings at the warren', 'emberwild', -60, -24),
      ),
    ],
    offer: [
      'Now, about that cart. Something dragged half my crates off it in the night. Little clawed feet. Dozens of them.',
      'Grubling tracks, running north over the ford and west to the old warren. Lyra will know what to make of them.',
      "Follow them, thump whatever is wearing my crates, and tell Lyra what you find. I'll be here. Counting what's left.",
    ],
    progress: 'Grublings. Across the ford, then west. Thump them.',
    complete: [
      'Grublings? This far south? They never leave the warren in daylight.',
      "Look at their claws: shard-dust, glittering. They're not raiding for food. They're following the light, same as the wolves.",
    ],
    rewards: mainRewards(4, 0.6, { gear: 'rare' }),
  }),
  main({
    id: 'mq_007',
    name: 'Purge the Warren',
    level: 5,
    zone: 'emberwild',
    giver: 'scout_lyra',
    turnIn: 'scout_lyra',
    requires: ['mq_006'],
    steps: [
      step(
        undefined,
        kill('grubling', 10, 'Slay Grublings in the warren', 'emberwild', -46, -40),
        kill('grubling_brute', 1, 'Defeat a Grubling Brute', 'emberwild', -76, -36),
      ),
    ],
    offer: [
      'If shard-light is pulling them out, the whole warren will swarm the vale by the new moon. We hit them first.',
      'The big ones, the brutes, keep the others in line. Take one down and the rest scatter.',
      "I'll watch the ford. Clear the warren and bring me anything that glows.",
    ],
    progress: 'The warren is west of the ford. The brutes lair at its far end.',
    complete: [
      'The brute wore this on a cord: a flake of shard-glass. And look at the scratchings on its bone bead...',
      "A grubling in a crown, holding a club that shines. They have a chieftain, and he's found a shard.",
      "There's one place in the vale old enough to hide something like that: the shrine ruins to the north-west.",
    ],
    rewards: mainRewards(5, 0.55),
  }),
  main({
    id: 'mq_008',
    name: 'The Shrine of the First Flame',
    level: 6,
    zone: 'emberwild',
    giver: 'scout_lyra',
    turnIn: 'captain_brenna',
    requires: ['mq_007'],
    steps: [
      step(undefined, reach('emberwild', -34, -64, 10, 'Reach the Shrine of the First Shard')),
      step(
        'The altar is cold, but the tablet beside it glows faintly as you approach.',
        interact('ew_lore_shrine', 'Read the shrine tablet', 'emberwild', -36, -66),
      ),
    ],
    offer: [
      'Past the warren, up on the rise, there is a ring of broken pillars. Folk call it the Shrine of the First Shard. Nobody goes there.',
      'My mother said the first Lantern-bearers kindled their flame there, before Havenreach was even a village. There is a tablet by the altar.',
      "Read it, then take whatever it says to Brenna. She'll pretend not to believe it. Then she'll believe it.",
    ],
    progress:
      'The shrine is north-west, on the rise past the warren. Read the tablet by the altar.',
    complete: [
      "'Here the first flame was lit, and here its bearers sleep, in the root-halls beneath.' The Hollowroot Crypt. My grandmother used that name to scare us to bed.",
      "So the grublings dug into the old bearers' tomb and their chieftain found a shard down there. That's not a raid anymore. That's a war-band.",
      'We go down there. But not yet. First I need the north road open, or nobody comes to help us when it goes wrong.',
    ],
    rewards: mainRewards(6, 0.55, { items: items(['potion_small', 3]) }),
  }),
  main({
    id: 'mq_009',
    name: 'Shard Peddlers',
    level: 8,
    zone: 'emberwild',
    giver: 'captain_brenna',
    turnIn: 'captain_brenna',
    requires: ['mq_008'],
    steps: [
      step(
        undefined,
        kill('vale_bandit', 6, 'Defeat Vale Bandits at the hideout', 'emberwild', 62, -66),
        kill('bandit_archer', 3, 'Defeat Bandit Archers', 'emberwild', 62, -66),
      ),
    ],
    offer: [
      'Bandits have dug in north-east of the ford, where the old watch-post stood. They stop anyone on the north road.',
      "Worse, they're buying shard-glass. Paying good coin to anyone desperate enough to dig it out of the craters.",
      "If someone is building a market for the Lantern's pieces, I want to know who's buying. Break their line.",
    ],
    progress: 'The hideout is north-east of the ford. Tents, carts, and far too many arrows.',
    complete: [
      "They're scattering. Good. One of them babbled before he ran: the shard-glass goes into a strongbox at the back of the camp.",
      'And every new moon, someone collects it. Someone in a mask.',
    ],
    rewards: mainRewards(8, 0.5),
  }),
  main({
    id: 'mq_010',
    name: 'The Strongbox',
    level: 8,
    zone: 'emberwild',
    giver: 'captain_brenna',
    turnIn: 'captain_brenna',
    requires: ['mq_009'],
    steps: [
      step(
        undefined,
        kill('vale_bandit', 4, 'Deal with the strongbox guards', 'emberwild', 80, -80),
        interact('ew_bandit_chest', "Open the bandits' strongbox", 'emberwild', 72, -82),
      ),
    ],
    offer: [
      "That strongbox sits at the back of their hideout, past the carts. Whatever's inside is the only lead we'll get.",
      'Open it, take what you find, and bring me anything with writing on it.',
    ],
    progress:
      'The strongbox is at the back of the bandit hideout. Bring me anything with writing on it.',
    complete: [
      "A letter. No name, only a seal pressed in black glass. 'Every sliver of the Lantern, delivered to the Drowned Stair at the new moon. The Hollow pays in kind.'",
      'The Hollow pays. Someone out there is buying the Lantern back for the dark, piece by piece.',
      "I'll send a copy to Havenreach with the next rider. Keep the original. Something tells me you'll meet whoever wrote it.",
    ],
    rewards: mainRewards(8, 0.55, { gear: 'rare' }),
  }),
  main({
    id: 'mq_011',
    name: 'The Elder of the Vale',
    level: 10,
    zone: 'emberwild',
    giver: 'captain_brenna',
    turnIn: 'scout_lyra',
    requires: ['mq_010'],
    requiresLevel: 8,
    steps: [
      step(
        undefined,
        kill('elder_thornback', 1, 'Defeat the Elder Thornback', 'emberwild', 58, 6, 16),
      ),
    ],
    offer: [
      'One more thing before the crypt, and it is a big one. The Elder Thornback, the old boar king of the vale, has gone mad.',
      "Lyra tracked him to the clearing east of the ford. He's gored two of her hunters and trampled the east road flat.",
      "He's older than this outpost and twice as stubborn. Bring potions. Bring friends, if you have them.",
    ],
    progress: 'The Elder Thornback paws at the clearing east of the ford. Bring potions.',
    complete: [
      'He choked on this. A splinter of shard-glass, big as my thumb, lodged behind his tusk.',
      'Poor old brute. I used to leave him apples when I was little. The whole vale is hurting since the sky fell.',
      "It's time. The crypt. And this time I'm coming as far as the stair.",
    ],
    rewards: mainRewards(10, 0.45, { items: items(['potion_small', 4]) }),
  }),
  main({
    id: 'mq_012',
    name: 'Into the Hollowroot',
    level: 9,
    zone: 'emberwild',
    giver: 'scout_lyra',
    turnIn: 'scout_lyra',
    requires: ['mq_011'],
    requiresLevel: 7,
    steps: [
      step(
        undefined,
        clearDungeon(
          'crypt',
          'Defeat the Grubling Chieftain in the Hollowroot Crypt',
          'emberwild',
          -84,
          -70,
        ),
      ),
    ],
    offer: [
      "The crypt stair is at the end of the west trail, past the shrine. I'll hold the entrance and make sure nothing follows you down.",
      'The Chieftain will be at the bottom, in the deepest hall. They always put their kings at the bottom.',
      'Bring that shard back. Bring yourself back. In that order if Brenna asks, but in the other order if I do.',
    ],
    progress: "The stair is at the end of the west trail. I'll be at the top when you come up.",
    complete: [
      "You're back! And that... that's the shard. The whole thing, not a splinter. It's singing. Can you hear it?",
      'The Chieftain had fused it right into his club. No wonder the grublings followed him into the dark.',
      'Go on, show Brenna. I want to see her face.',
    ],
    rewards: mainRewards(9, 0.5, { gear: 'epic', flags: ['act1_crypt'] }),
  }),
  main({
    id: 'mq_013',
    name: 'The Ember Shard',
    level: 10,
    zone: 'emberwild',
    giver: 'scout_lyra',
    turnIn: 'captain_brenna',
    requires: ['mq_012'],
    steps: [step(undefined, talk('captain_brenna', 'Bring the Ember Shard to Captain Brenna'))],
    offer: [
      "She's at the fire. She's been at the fire all day, pretending not to watch the trail.",
    ],
    complete: [
      "So that's what three hundred years of Lantern-light looks like up close. Seven of these kept the Hollow shut, and you've brought one home.",
      'Havenreach needs to see this. You need to see Havenreach. High Warden Aldara will know what a Shardbearer is for.',
      "I've written you a letter so the gate wardens don't arrest you on sight. Take the north road. And... thank you. The vale owes you.",
    ],
    rewards: mainRewards(10, 0.35, {
      card: 'c_brenna',
      bond: 40,
      items: items(['skill_potion', 1]),
      flags: ['act1_complete'],
    }),
  }),
  main({
    id: 'mq_014',
    name: 'The Road to Havenreach',
    level: 10,
    zone: 'havenreach',
    giver: 'captain_brenna',
    turnIn: 'high_warden_aldara',
    requires: ['mq_013'],
    steps: [
      step(undefined, arrive('havenreach', 'Travel north to Havenreach')),
      step(
        "The city of the Lantern Watch. At the top of the processional stands the Warden's Keep.",
        talk('high_warden_aldara', "Deliver Brenna's letter to High Warden Aldara"),
      ),
    ],
    offer: [
      "North road, through the city gate. Can't miss it: biggest walls on the isles, and the only Waystone Dais worth the name.",
      "The High Warden holds court at the Warden's Keep, top of the processional. Give her my letter before you do anything else.",
    ],
    complete: [
      "Brenna's hand. She never did learn to write small. 'Bearer of the falling light'... So the rumour walks on two legs.",
      'Let me see the shard. ...It knows you. The Lantern chose its bearers the same way, once, before we forgot how.',
      'I am Aldara, High Warden of the Lantern Watch. What is left of it. Welcome to Havenreach, Shardbearer.',
    ],
    rewards: mainRewards(10, 0.35, { bond: 20 }),
  }),

  // ================================================================ INTERLUDE — Havenreach
  main({
    id: 'mq_015',
    name: 'City of the Lantern Watch',
    level: 11,
    zone: 'havenreach',
    giver: 'high_warden_aldara',
    turnIn: 'high_warden_aldara',
    requires: ['mq_014'],
    steps: [
      step(
        undefined,
        interact(
          'hr_waystone_plaza',
          'Attune to the waystone on the Plaza Dais',
          'havenreach',
          0,
          -11,
        ),
        interact(
          'hr_lore_statue',
          "Read the Bearers' Oath beneath the statue",
          'havenreach',
          -8,
          -15.5,
        ),
      ),
    ],
    offer: [
      'Before I send you anywhere, learn this city. Its roads will carry you further than any horse.',
      'Go down to the Waystone Dais in the plaza and attune yourself to it. Every waystone you touch becomes a door you can open later.',
      "Then read the oath carved beneath the bearer's statue behind it. Every Warden has sworn it. You should know what we promised.",
    ],
    progress: 'The Dais is in the plaza, below the Keep. The statue stands just behind it.',
    complete: [
      "'While one light burns, the dark below shall wait.' Three hundred years we kept that promise. Three nights ago we broke it.",
      'The waystones answer to you now. Use them. Walking is for pilgrims and people with more time than we have.',
      'I have sent Borin shards and ore enough for a first tempering. Carry them to him.',
    ],
    rewards: mainRewards(11, 0.45, {
      items: items(
        ['aether_shard', 80],
        ['ore_armor', 12],
        ['ore_weapon', 12],
        ['leap_crystal', 3],
      ),
    }),
  }),
  main({
    id: 'mq_016',
    name: 'Tempered Steel',
    level: 11,
    zone: 'havenreach',
    giver: 'high_warden_aldara',
    turnIn: 'smith_borin',
    requires: ['mq_015'],
    steps: [step(undefined, flag('tut_tempered', "Temper a piece of equipment at Borin's forge"))],
    offer: [
      'Your gear has kept you alive. It will not keep you alive against what is coming.',
      "Borin's forge is in the south-west quarter; you'll hear it before you see it. The shards and ore I gave you will pay for a first tempering.",
      'Temper one piece, any piece. Learn what shard-steel feels like when it takes the light.',
    ],
    progress: 'Speak to Borin and choose "Temper equipment". Any piece will do.',
    complete: [
      "Hear that ring? That's steel that remembers being struck, and decided to be harder for it.",
      "Tempering eats shards and ore, and sometimes it fails. Don't sulk when it does. Every failure makes the next strike surer.",
      "Come back when you've more to temper. I don't talk much. I listen to steel.",
    ],
    rewards: mainRewards(11, 0.45, {
      card: 'c_borin',
      items: items(['aether_shard', 60], ['ore_armor', 10], ['ore_weapon', 6]),
    }),
  }),
  main({
    id: 'mq_017',
    name: 'Coin and Contracts',
    level: 11,
    zone: 'havenreach',
    giver: 'smith_borin',
    turnIn: 'high_warden_aldara',
    requires: ['mq_016'],
    steps: [
      step(
        undefined,
        talk('bounty_master_quill', 'Meet Quill at the bounty board in Gate Square'),
        talk('broker_venn', 'Meet Venn at the market'),
      ),
    ],
    offer: [
      'Warden said to send you round the city after. Quill runs the bounty board in Gate Square. Venn runs the market.',
      'Quill pays for dead things. Venn pays for live things, and charges more. Talk to both. Then back to the Warden.',
    ],
    complete: [
      "Quill's board will keep your purse full, and Venn's stalls will empty it again. That is how a city breathes.",
      'Now. There is someone who has waited for that shard even longer than I have.',
    ],
    rewards: mainRewards(11, 0.4),
  }),
  main({
    id: 'mq_018',
    name: 'The Scholar of Rifts',
    level: 12,
    zone: 'havenreach',
    giver: 'high_warden_aldara',
    turnIn: 'rift_scholar_elowen',
    requires: ['mq_017'],
    steps: [
      step(undefined, talk('rift_scholar_elowen', 'Show the Ember Shard to Elowen in Gate Square')),
    ],
    offer: [
      'Elowen studies the rifts from the old portal frame in Gate Square. She was a Watch scholar before there was a Watch left to study for.',
      'Show her the Ember Shard. If anyone can tell us where the other six fell, it is her.',
    ],
    complete: [
      "May I? ...Oh. Oh, it's resonating. Hold it still and you can almost hear it.",
      'Every shard hums at the same pitch, and something is humming back. Far to the east, past the desert road.',
      'Sunscar. The second shard fell in Sunscar, and the signal is strong. Something big is holding on to it.',
    ],
    rewards: mainRewards(12, 0.35, { items: items(['potion_major', 3]) }),
  }),

  // ================================================================ ACT II — Sunscar Expanse
  main({
    id: 'mq_019',
    name: 'Where the Caravans Vanish',
    level: 12,
    zone: 'sunscar',
    giver: 'rift_scholar_elowen',
    turnIn: 'caravan_master_hadi',
    requires: ['mq_018'],
    requiresLevel: 12,
    steps: [
      step(undefined, arrive('sunscar', 'Travel east to the Sunscar Expanse')),
      step(
        'Canyons, ruins and a very great deal of sand. The caravan camp lies on the trail.',
        talk('caravan_master_hadi', 'Find Caravan Master Hadi at the caravan camp'),
      ),
    ],
    offer: [
      'The Sunscar gate is at the end of the market street. Beyond it lies the Expanse: canyons, ruins and a very great deal of sand.',
      'Caravan Master Hadi keeps the last camp on the trail. His caravans have been vanishing along the very road my readings point to.',
      "I'd call that a clue. Take water. Take two waters.",
    ],
    complete: [
      "You're the one the city sent? Good. Three caravans gone in a week, friend, and not a scrap of cloth left behind.",
      "The dunes are hungry this season. Hungrier than I've seen in thirty years of crossing them.",
      "I can't pay much. But I can give you a tent, water and every rumour this camp has heard. Out here, that's worth more.",
    ],
    rewards: mainRewards(12, 0.4, { flags: ['act2_started'] }),
  }),
  main({
    id: 'mq_020',
    name: 'Stingers on the Trail',
    level: 13,
    zone: 'sunscar',
    giver: 'caravan_master_hadi',
    turnIn: 'caravan_master_hadi',
    requires: ['mq_019'],
    steps: [
      step(
        undefined,
        kill('sand_scorpion', 8, 'Slay Sand Scorpions along the town trail', 'sunscar', -50, -58),
      ),
    ],
    offer: [
      "First things first: my scouts can't even leave camp. Sand scorpions have nested along the town trail, bold as bandits.",
      'Clear them out so my riders can move. Then we talk about the missing caravans.',
    ],
    progress: 'The scorpions nest along the trail north of camp, toward the city.',
    complete: [
      "The trail's open. My riders say you fight like someone who's never had to pay for water.",
      "Now, the caravans. I have a theory, but you should hear Zahra's first. Hers is stranger, and it's usually right.",
    ],
    rewards: mainRewards(13, 0.35),
  }),
  main({
    id: 'mq_021',
    name: 'The Seer of the Dunes',
    level: 13,
    zone: 'sunscar',
    giver: 'caravan_master_hadi',
    turnIn: 'nomad_seer_zahra',
    requires: ['mq_020'],
    steps: [step(undefined, talk('nomad_seer_zahra', 'Speak with Zahra, the nomad seer'))],
    offer: [
      "Zahra reads the sand. Don't laugh; she found the oasis spring when our dowsers gave up.",
      "She's been asking for you since before you arrived. By a name none of us know.",
    ],
    complete: [
      'Ember-child. The sand told me you would come with a star in your hand. It did not tell me you would look so tired.',
      'Sit. Listen. The caravans did not vanish. They were taken, south-west, where the raiders make their fires.',
      "And beneath the raiders' fires, the sand sings the same song as your shard. Something below is awake.",
    ],
    rewards: mainRewards(13, 0.25, { bond: 20 }),
  }),
  main({
    id: 'mq_022',
    name: "The Raiders' Toll",
    level: 14,
    zone: 'sunscar',
    giver: 'nomad_seer_zahra',
    turnIn: 'caravan_master_hadi',
    requires: ['mq_021'],
    steps: [
      step(
        undefined,
        kill('dune_raider', 6, 'Defeat Dune Raiders at the red tents', 'sunscar', -54, 70),
        kill('raider_slinger', 3, 'Defeat Raider Slingers', 'sunscar', -54, 70),
        interact(
          'ss_chest_raiders',
          "Recover Hadi's ledgers from the raiders' strongbox",
          'sunscar',
          -60,
          81,
        ),
      ),
    ],
    offer: [
      "The dune raiders camp in the south-west, among the red tents. They wear the caravans' cloth now.",
      "Break their camp. In their strongbox you will find Hadi's missing ledgers. Bring them to him.",
    ],
    progress: 'The red tents stand in the south-west. The strongbox is at the back of the camp.',
    complete: [
      "My manifests! Every caravan they took... 'sold on to the masked ones, to be carried down the Drowned Stair.'",
      "Masked ones. The Drowned Stair. Friend, your vale bandits had a letter with those same words, didn't they?",
      "Someone is buying people and shards alike. I'll send riders to warn the other camps.",
    ],
    rewards: mainRewards(14, 0.4, { gear: 'rare' }),
  }),
  main({
    id: 'mq_023',
    name: 'Bones Beneath the Sand',
    level: 15,
    zone: 'sunscar',
    giver: 'caravan_master_hadi',
    turnIn: 'nomad_seer_zahra',
    requires: ['mq_022'],
    steps: [
      step(
        undefined,
        kill(
          'sunscar_skeleton',
          8,
          'Destroy the Sunscar Skeletons north of the crossroads',
          'sunscar',
          -4,
          -40,
        ),
      ),
    ],
    offer: [
      "As if raiders weren't enough. My night watch swears they saw the dead walking north of the crossroads, in armour older than my grandfather's grandfather.",
      "I'd call them drunk, but they've all gone pale and sober. Go and see. And if the dead are walking, stop them. Then tell Zahra; she'll know what it means.",
    ],
    progress: 'The dead walk north of the crossroads, on the road to the ruins.',
    complete: [
      "Sandsworn dead. The old queen's soldiers, called up from their long sleep.",
      "Hadi thinks they walked out of the ruins. They did not walk. They were summoned, by what fell into their queen's tomb.",
    ],
    rewards: mainRewards(15, 0.35),
  }),
  main({
    id: 'mq_024',
    name: 'The Sandsworn Edict',
    level: 16,
    zone: 'sunscar',
    giver: 'nomad_seer_zahra',
    turnIn: 'nomad_seer_zahra',
    requires: ['mq_023'],
    steps: [
      step(
        undefined,
        kill(
          'sunscar_skeleton',
          6,
          'Destroy the skeletons guarding the colonnade',
          'sunscar',
          30,
          -72,
        ),
        interact(
          'ss_lore_colonnade',
          "Read the queen's edict among the columns",
          'sunscar',
          23,
          -85,
        ),
        interact(
          'ss_waystone_colonnade',
          'Attune to the Sunken Colonnade waystone',
          'sunscar',
          14,
          -50,
        ),
      ),
    ],
    offer: [
      "North, where the road climbs to the colonnade, the Sandsworn carved their laws in stone. Read the queen's edict.",
      'Touch the waystone there as well. The ruins are far, and you will walk them often before this ends.',
      'The dead guard the colonnade. They will not let you read in peace.',
    ],
    progress: 'The colonnade lies north, where the tomb road climbs. The waystone stands below it.',
    complete: [
      "'Let the Warden of Sand keep my rest, and let none pass who do not carry the light.' The edict of Queen Ishmera.",
      "The Warden is her tomb's guardian, a giant of living sand. The fallen star woke it, and it has woken her soldiers.",
      'Only one who carries the light may pass the tomb door. Ember-child, that is you. But not yet. You are not ready, and neither is the door.',
    ],
    rewards: mainRewards(16, 0.35, { items: items(['potion_major', 3]) }),
  }),
  main({
    id: 'mq_025',
    name: 'A Letter by Hawk',
    level: 15,
    zone: 'havenreach',
    giver: 'nomad_seer_zahra',
    turnIn: 'harbormaster_sol',
    requires: ['mq_024'],
    requiresLevel: 15,
    steps: [
      step(undefined, talk('harbormaster_sol', 'Meet Harbormaster Sol at the Havenreach docks')),
    ],
    offer: [
      "A hawk came for you this morning, wearing the Warden's colours. Hawks are rude. It bit Hadi.",
      'High Warden Aldara has a gift for you at the Havenreach docks. Go. The tomb has waited a thousand years; it can wait a few days more.',
    ],
    complete: [
      "Ahoy! You're the Shardbearer? Thought you'd be taller. The Warden's paid for a ship in your name, and a good one too.",
      "She's rigged, provisioned and pointed at open water. Haven Isle is waiting for you, north-west across the Gleaming Sea.",
      'Old Watch outpost out there. Been empty for years. Warden says a Shardbearer needs a hearth of their own.',
    ],
    rewards: mainRewards(15, 0.25, { flags: ['sailing_unlocked'] }),
  }),
  main({
    id: 'mq_026',
    name: 'Set Sail',
    level: 16,
    zone: 'haven',
    giver: 'harbormaster_sol',
    turnIn: 'steward_pim',
    requires: ['mq_025'],
    requiresLevel: 15,
    steps: [
      step(undefined, arrive('sea', 'Board your ship at the north pier and set sail')),
      step(
        'Open water, bright and endless. Haven Isle lies to the north-west.',
        arrive('haven', 'Sail to Haven Isle'),
      ),
      step(
        'An old Watch outpost, overgrown and quiet. Someone is waving from the lodge.',
        talk('steward_pim', 'Meet Steward Pim'),
      ),
    ],
    offer: [
      "Walk up the gangway on the north pier and she's yours. Steer clear of the storm fronts; the sea's been restless since the sky broke.",
      "Haven Isle's dock is north-west. My cousin Pim keeps the place. Talks a lot. Means well.",
    ],
    complete: [
      "Oh! Oh, you're here! You're the Shardbearer! I'm Pim, your steward. Welcome to Haven Isle!",
      "The workshops are dusty and the garden's mostly weeds, but the bones are good, and the research table still has the Watch's old notes.",
      'Workshops, research, a garden and a dispatch dock for crews. All yours. Well, ours. Mostly yours.',
    ],
    rewards: mainRewards(16, 0.35, { flags: ['haven_unlocked'] }),
  }),
  main({
    id: 'mq_027',
    name: 'Back to the Dunes',
    level: 16,
    zone: 'sunscar',
    giver: 'steward_pim',
    turnIn: 'nomad_seer_zahra',
    requires: ['mq_026'],
    steps: [step(undefined, talk('nomad_seer_zahra', 'Return to Zahra in Sunscar'))],
    offer: [
      "A gull brought this, addressed to 'the one with the star-hand'. It's from a seer in Sunscar. She draws very good gulls.",
      "She says the oasis is dying and you should come quickly. Don't worry about the isle; I'll keep the lamps lit.",
    ],
    complete: [
      'The sea has salted you. Good. Salt keeps things.',
      'Listen: the oasis is dying. Raiders drain it, and something vast moves beneath the southern sand.',
    ],
    rewards: mainRewards(16, 0.2),
  }),
  main({
    id: 'mq_028',
    name: 'The Oasis Under Siege',
    level: 17,
    zone: 'sunscar',
    giver: 'nomad_seer_zahra',
    turnIn: 'caravan_master_hadi',
    requires: ['mq_027'],
    steps: [
      step(
        undefined,
        kill('dune_raider', 6, 'Drive the Dune Raiders from the oasis', 'sunscar', -22, 62),
        kill('raider_slinger', 2, 'Defeat Raider Slingers at the oasis', 'sunscar', -22, 62),
        kill('sand_scorpion', 5, 'Slay the scorpions following them', 'sunscar', 30, 30),
      ),
    ],
    offer: [
      'Raiders have taken the oasis, and the scorpions follow them for the scraps. Drive them all out.',
      "Hadi's people drink from that spring. Report to him when it runs clean again.",
    ],
    progress: 'The oasis lies south of the crossroads. The raiders camp on its western bank.',
    complete: [
      'The oasis is ours again! My water-bearers are singing, which is worse than silence, believe me.',
      'But the oasis folk say something enormous burrows in the southern sand, east of the spring. They call it Dunemaw.',
      "It's eaten three wells and a watchtower. Now it's eating the road.",
    ],
    rewards: mainRewards(17, 0.4, { card: 'c_hadi' }),
  }),
  main({
    id: 'mq_029',
    name: 'Dunemaw',
    level: 19,
    zone: 'sunscar',
    giver: 'caravan_master_hadi',
    turnIn: 'nomad_seer_zahra',
    requires: ['mq_028'],
    requiresLevel: 17,
    steps: [
      step(
        undefined,
        kill('dunemaw', 1, 'Defeat Dunemaw in the sand arena', 'sunscar', 46, 72, 16),
      ),
    ],
    offer: [
      "Dunemaw hunts in the old sand arena south-east of the oasis, ringed with broken pillars. You'll know it by the bones.",
      "It's bigger than a caravan and angrier than a camel. Please don't die. I've grown fond of you.",
    ],
    progress: 'The sand arena lies south-east of the oasis. Follow the bones.',
    complete: [
      "It swallowed a sliver of the tomb's star. See how the glass drinks your light? It points north, to the tomb door.",
      'Now the door will hear you. Now you are ready.',
    ],
    rewards: mainRewards(19, 0.3, { items: items(['potion_major', 4]) }),
  }),
  main({
    id: 'mq_030',
    name: 'The Sealed Door',
    level: 17,
    zone: 'sunscar',
    giver: 'nomad_seer_zahra',
    turnIn: 'nomad_seer_zahra',
    requires: ['mq_029'],
    requiresLevel: 17,
    steps: [
      step(
        undefined,
        kill('sunscar_skeleton', 6, 'Destroy the dead gathered below the tomb', 'sunscar', 12, -88),
        kill('sand_golem', 1, 'Shatter the stone guardian', 'sunscar', 12, -88),
        reach('sunscar', 52, -90, 9, "Stand before the Sandsworn Tomb's door"),
      ),
    ],
    offer: [
      "The tomb door lies north of the colonnade, cut into the cliff. The queen's dead gather thickest there, and a guardian of stone stands with them.",
      'Clear the way and stand before the door. It will open for the light you carry, and for no one else.',
    ],
    progress: 'The tomb door is cut into the northern cliff, past the colonnade.',
    complete: [
      'I felt it open from here. The whole desert let out a breath it has held for a thousand years.',
      'Now comes the hard part, ember-child.',
    ],
    rewards: mainRewards(17, 0.35),
  }),
  main({
    id: 'mq_031',
    name: 'The Sandbound Warden',
    level: 18,
    zone: 'sunscar',
    giver: 'nomad_seer_zahra',
    turnIn: 'nomad_seer_zahra',
    requires: ['mq_030'],
    requiresLevel: 17,
    steps: [
      step(
        undefined,
        clearDungeon(
          'tomb',
          'Still the Sandbound Warden in the Sandsworn Tomb',
          'sunscar',
          52,
          -98,
        ),
      ),
    ],
    offer: [
      "Below the door waits the Warden of Sand, the queen's last guardian. It holds the second shard in its chest like a heart.",
      'It is not evil. It is only faithful to the wrong thing now. Still it, and bring the shard into the light.',
    ],
    progress: 'The Warden waits below the tomb door. Carry the light down to it.',
    complete: [
      'Two lights now. The sand is quiet for the first time since the sky broke.',
      'The Dune Shard. Hold it beside the Ember Shard... see how they lean toward each other? They remember being one lantern.',
      'Rest a moment. Then look north-east. The shards are pulling toward the cold.',
    ],
    rewards: mainRewards(18, 0.4, {
      gear: 'epic',
      card: 'c_zahra',
      bond: 40,
      items: items(['skill_potion', 1], ['book_brutal_edge', 1]),
      flags: ['act2_tomb'],
    }),
  }),
  main({
    id: 'mq_032',
    name: 'The Canyon Road',
    level: 20,
    zone: 'sunscar',
    giver: 'nomad_seer_zahra',
    turnIn: 'caravan_master_hadi',
    requires: ['mq_031'],
    steps: [
      step(
        undefined,
        kill(
          'dune_raider',
          6,
          'Defeat the raiders holding the Scorched Canyon',
          'sunscar',
          64,
          -26,
        ),
        kill('sand_golem', 2, 'Shatter the golems blocking the road', 'sunscar', 82, -34),
        interact(
          'ss_waystone_canyon',
          'Attune to the Scorched Canyon waystone',
          'sunscar',
          90,
          -16,
        ),
      ),
    ],
    offer: [
      'The road north-east runs through the Scorched Canyon, and the canyon belongs to the raiders now, and to stone that walks.',
      'Hadi means to send a caravan north the moment the way is clear. Clear it, touch the canyon waystone, and tell him.',
    ],
    progress:
      'The Scorched Canyon runs east along the caravan road. The waystone stands at its far end.',
    complete: [
      "The canyon's clear? Then the first caravan north in a month leaves at dawn, and it carries a Shardbearer.",
      "Frosthollow's people have been living on dried fish and prayer. They'll be glad of our spices. And of you.",
    ],
    rewards: mainRewards(20, 0.4, { gear: 'rare' }),
  }),
  main({
    id: 'mq_033',
    name: 'A Bell in the North',
    level: 21,
    zone: 'frosthollow',
    giver: 'caravan_master_hadi',
    turnIn: 'abbess_vira',
    requires: ['mq_032'],
    requiresLevel: 21,
    steps: [
      step(undefined, arrive('frosthollow', 'Cross the eastern pass into Frosthollow')),
      step(
        'Snow, pines and the distant toll of a bell. The abbey lies at the foot of the pass.',
        talk('abbess_vira', 'Speak with Abbess Vira at the Abbey of the Hollow Bell'),
      ),
    ],
    offer: [
      'A rider came down from the north last night, half frozen. The Abbey of the Hollow Bell has rung its bell three nights running.',
      "Their abbess asked for 'whoever carries the falling light'. Take the canyon road east. Dress warmer than you think.",
    ],
    complete: [
      'So you are the light the sand-seer wrote of. You are late, Shardbearer. The bell has rung three nights running.',
      'The Hollow Bell rings when the dead walk. It has not rung in my lifetime. It has not stopped since the Aetherfall.',
      'I am Vira, abbess of this house. We keep vigil for the dead. Tonight, I fear, the dead keep vigil for us.',
    ],
    rewards: mainRewards(21, 0.35, { flags: ['act3_started'] }),
  }),

  // ================================================================ ACT III — Frosthollow
  main({
    id: 'mq_034',
    name: 'Wolves of the Pass',
    level: 22,
    zone: 'frosthollow',
    giver: 'abbess_vira',
    turnIn: 'hunter_bjorn',
    requires: ['mq_033'],
    steps: [
      step(
        undefined,
        kill('frost_wolf', 8, 'Slay Frost Wolves on the pass', 'frosthollow', -97, 30),
      ),
    ],
    offer: [
      'Our sledges cannot reach us from the pass. Frost wolves hunt the road in packs, and they do not fear fire.',
      'Bjorn hunts for the abbey. He will be glad of a second blade, though he will not say so.',
    ],
    progress: 'The wolves hunt the pass road, west of the abbey.',
    complete: [
      'Clean kills.',
      'These wolves are herded. Something with a louder howl drives them south. Watched three nights. Never seen it.',
    ],
    rewards: mainRewards(22, 0.45),
  }),
  main({
    id: 'mq_035',
    name: 'Restless Graves',
    level: 23,
    zone: 'frosthollow',
    giver: 'hunter_bjorn',
    turnIn: 'abbess_vira',
    requires: ['mq_034'],
    steps: [
      step(
        undefined,
        kill('frost_ghoul', 8, 'Put the risen dead back to rest', 'frosthollow', -18, 54),
        interact('fh_lore_graves', "Read the bell-ringer's gravestone", 'frosthollow', -22, 30),
      ),
    ],
    offer: [
      'Abbess wants you at the graveyard. South-east of the abbey.',
      "Dead are climbing out. Frost in their eyes, like the wolves. Put them back. Read the old ringer's stone while you're there.",
    ],
    progress: "Graveyard. South-east. Ringer's stone is by the altar.",
    complete: [
      'Brother Hesk, the last of our bell-ringers. He rang the Hollow Bell until his hands froze to the rope, the winter the lake first froze.',
      "His stone says he rang to warn us of 'the cold that thinks'. We thought it was a fever dream. Now his dead do not rest.",
      'Take this codex. The sigil-scribes of Havenreach bind strength into ink; read it, and the ink will remember you.',
    ],
    rewards: mainRewards(23, 0.45, { bond: 20, items: items(['book_composure', 1]) }),
  }),
  main({
    id: 'mq_036',
    name: 'The Drowned Bell',
    level: 24,
    zone: 'frosthollow',
    giver: 'abbess_vira',
    turnIn: 'abbess_vira',
    requires: ['mq_035'],
    steps: [
      step(
        undefined,
        kill(
          'ice_wraith',
          6,
          'Banish the Ice Wraiths at the drowned chapel',
          'frosthollow',
          18,
          -48,
        ),
        interact(
          'fh_lore_chapel',
          "Read the carving on the chapel's altar",
          'frosthollow',
          20,
          -55.5,
        ),
      ),
    ],
    offer: [
      'The first Hollow Bell hung in a chapel on the north shore of the lake. When the lake froze, the ice cracked the chapel and the bell sank.',
      'The wraiths have gathered there. And last night, beneath the ice, the drowned bell answered ours.',
      'Go to the drowned chapel. Banish the wraiths and read what its builders carved on the altar.',
    ],
    progress: 'The drowned chapel stands on the north shore of the frozen lake.',
    complete: [
      "'Ring to the living, and the dead shall hear. Ring to the dead, and the Hollow shall answer.'",
      'Someone is ringing the drowned bell to call the Hollow up through the ice. The rift is under the lake.',
    ],
    rewards: mainRewards(24, 0.45, { gear: 'rare' }),
  }),
  main({
    id: 'mq_037',
    name: 'Rift in the Ice',
    level: 25,
    zone: 'frosthollow',
    giver: 'abbess_vira',
    turnIn: 'hunter_bjorn',
    requires: ['mq_036'],
    steps: [
      step(
        undefined,
        kill('ice_wraith', 4, 'Banish the wraiths on the east shore', 'frosthollow', 46, -44),
        kill('frost_ghoul', 6, 'Destroy the dead crawling from the ice', 'frosthollow', 46, -44),
      ),
    ],
    offer: [
      'If the Hollow comes up through the ice, it comes up on the east shore, where the lake is thinnest. Wraiths have been seen there.',
      'Break whatever gathers there, then tell Bjorn what you saw. He will know where they march.',
    ],
    progress: 'The east shore of the lake, where the ice is thinnest.',
    complete: [
      'Saw them from the ridge. Came out of the ice in rows. Black armour. No breath.',
      "Hollow Legion. Marching north to the old keep. Frostfang. Nobody's lived there in a hundred years. Something lives there now.",
    ],
    rewards: mainRewards(25, 0.45),
  }),
  main({
    id: 'mq_038',
    name: 'The Overlook',
    level: 26,
    zone: 'frosthollow',
    giver: 'hunter_bjorn',
    turnIn: 'abbess_vira',
    requires: ['mq_037'],
    steps: [
      step(
        undefined,
        kill('frost_ghoul', 6, 'Clear the dead from the north-west ruins', 'frosthollow', -82, -42),
        interact(
          'fh_vista_overlook',
          'Survey the Legion from the Hollow Bell Overlook',
          'frosthollow',
          -30,
          -84,
        ),
      ),
    ],
    offer: [
      'Need eyes on the keep road. Best view is the overlook, north-west, above the old ruins.',
      'Ruins are thick with dead. Clear a path. Climb up. Count what you see. Tell the abbess.',
    ],
    progress: 'Overlook. North, above the ruins. Count the columns.',
    complete: [
      'Three columns? And banners of black iron... The Legion answers to someone in that keep.',
      'The old chronicles name one ruler of Frostfang: the Frost Matriarch. She was once an abbess of this very house.',
      'Mother Isolde. She bargained with the Hollow to end a plague of cold, and the cold took her instead. We tell novices it is a parable.',
    ],
    rewards: mainRewards(26, 0.45, { items: items(['potion_elixir', 3]) }),
  }),
  main({
    id: 'mq_039',
    name: "The Alpha's Howl",
    level: 27,
    zone: 'frosthollow',
    giver: 'abbess_vira',
    turnIn: 'hunter_bjorn',
    requires: ['mq_038'],
    steps: [
      step(
        undefined,
        kill(
          'frost_wolf',
          6,
          'Scatter the frost wolves in the eastern woods',
          'frosthollow',
          40,
          36,
        ),
        kill('rimefang_alpha', 1, 'Defeat Rimefang Alpha in its den', 'frosthollow', 66, 50, 16),
      ),
    ],
    offer: [
      'Before we march on the keep, the packs must break. Bjorn says the wolves are driven by a single alpha.',
      'Hunt it. A leashed pack is a weapon; a broken one is only wolves.',
    ],
    progress: "The alpha's den lies in the eastern woods, beyond the lake.",
    complete: [
      "Rimefang. Biggest wolf I've ever seen. Look at the collar. Black iron. Legion work.",
      'They leashed the wolves like hounds. Now the packs will scatter. Good hunting, Shardbearer.',
    ],
    rewards: mainRewards(27, 0.4, { card: 'c_bjorn', items: items(['potion_elixir', 3]) }),
  }),
  main({
    id: 'mq_040',
    name: 'Highland Cairn',
    level: 27,
    zone: 'frosthollow',
    giver: 'hunter_bjorn',
    turnIn: 'abbess_vira',
    requires: ['mq_039'],
    steps: [
      step(
        undefined,
        kill('frost_yeti', 3, 'Drive off the yetis of the highlands', 'frosthollow', 54, -72),
        interact(
          'fh_waystone_highlands',
          'Attune to the Highland Cairn waystone',
          'frosthollow',
          74,
          -58,
        ),
      ),
    ],
    offer: [
      'Keep road climbs through the highlands. Yetis there. Big. Ill-tempered. Legion has been feeding them.',
      "Old cairn up top has a waystone. Touch it. You'll want a quick way back when the keep goes bad.",
    ],
    progress: 'Highlands. North-east. Below the keep.',
    complete: [
      'The highland waystone answers you? Then there is a road home from the keep, whatever happens there.',
      "The Matriarch's gate lies beyond. Rest, eat something hot, and pray, if you are the praying kind.",
    ],
    rewards: mainRewards(27, 0.4, { gear: 'rare' }),
  }),
  main({
    id: 'mq_041',
    name: 'The Frost Matriarch',
    level: 28,
    zone: 'frosthollow',
    giver: 'abbess_vira',
    turnIn: 'abbess_vira',
    requires: ['mq_040'],
    requiresLevel: 27,
    steps: [
      step(
        undefined,
        kill('frost_ghoul', 6, "Break the Legion's line on the keep road", 'frosthollow', 68, -88),
        kill('ice_wraith', 2, 'Banish the wraiths at the gate', 'frosthollow', 68, -88),
        reach('frosthollow', 86, -88, 8, 'Stand at the gate of Frostfang Keep'),
      ),
      step(
        'The gate of Frostfang Keep groans open before the light you carry.',
        clearDungeon(
          'keep',
          'Defeat the Frost Matriarch in Frostfang Keep',
          'frosthollow',
          86,
          -98,
        ),
      ),
    ],
    offer: [
      'It is time. The Legion guards the road to Frostfang Keep. Break their line and stand at the gate; it will not bar the light you carry.',
      'Inside waits the Frost Matriarch. Whatever Mother Isolde was, she is not that now. She holds the third shard.',
      'I will ring the bell for you. Not for the dead. For you.',
    ],
    progress:
      'Frostfang Keep stands north-east, beyond the highland cairn. The bell is ringing for you.',
    complete: [
      'You came back. The bell stopped ringing the moment she fell. I wept, and I am not ashamed of it.',
      'The Rime Shard. Three lights. Hold them close, Shardbearer. They are warm, even here.',
      'Rest by our fire tonight. Tomorrow we speak of what she said at the end. I heard it through the bell. So did you.',
    ],
    rewards: mainRewards(28, 0.35, {
      gear: 'epic',
      card: 'c_vira',
      bond: 40,
      items: items(['skill_potion', 1], ['book_vendetta', 1]),
      flags: ['act3_keep'],
    }),
  }),
  main({
    id: 'mq_042',
    name: 'The Tyrant Answers',
    level: 29,
    zone: 'havenreach',
    giver: 'abbess_vira',
    turnIn: 'high_warden_aldara',
    requires: ['mq_041'],
    steps: [
      step(undefined, talk('high_warden_aldara', 'Bring the three shards to High Warden Aldara')),
    ],
    offer: [
      "When she fell, a voice laughed through the ice. Not hers. Older. It called you 'little ember'.",
      "'Gather my Lantern's pieces as a child gathers sparks, and bring them to my throne.' Vhal'Zeroth. The Hollow Tyrant himself.",
      'Take the shards to Havenreach. Now. The High Warden must hear this from your own mouth.',
    ],
    complete: [
      'Three shards. And a voice from the Hollow that knows your name.',
      "Vhal'Zeroth wants us afraid. He wants us to hide the shards and wait. We will do neither.",
      'Three lights are not a Lantern. But they are enough to kindle one flame. Tonight, we light it.',
    ],
    rewards: mainRewards(29, 0.3, { bond: 30 }),
  }),

  // ================================================================ EPILOGUE — the first flame
  main({
    id: 'mq_043',
    name: 'The Rite of Kindling',
    level: 29,
    zone: 'havenreach',
    giver: 'high_warden_aldara',
    turnIn: 'rift_scholar_elowen',
    requires: ['mq_042'],
    steps: [
      step(
        undefined,
        talk('rift_scholar_elowen', 'Learn the rite of kindling from Elowen in Gate Square'),
      ),
    ],
    offer: [
      'The Lantern Cradle still stands in the heart of the Keep, empty since the Aetherfall. Elowen has spent three days preparing the rite.',
      'Go to her in Gate Square. She will tell you how the shards must be set.',
    ],
    complete: [
      "You came! Good. I've been talking to the portal frame for an hour and it refuses to answer.",
      'The rite is simple, the way terrifying things are simple: the shards go into the Cradle in the order they fell, and a bearer gives them light.',
      "Ember, Dune, Rime. Then you put your hand on the Cradle and... the texts get rather poetic after that. I think it means 'hold on'.",
    ],
    rewards: mainRewards(29, 0.2),
  }),
  main({
    id: 'mq_044',
    name: 'The First Flame',
    level: 29,
    zone: 'havenreach',
    giver: 'rift_scholar_elowen',
    turnIn: 'high_warden_aldara',
    requires: ['mq_043'],
    steps: [
      step(
        undefined,
        reach('havenreach', 0, -62, 9, "Stand before the Lantern Cradle in the Warden's Keep"),
      ),
    ],
    offer: [
      "Go on, the High Warden is waiting at the Cradle. I'll be right behind you, taking notes. From a safe distance.",
    ],
    progress:
      "The Cradle is in the Keep! Shards first, then your light. And please don't touch anything else.",
    complete: [
      'Set them in the Cradle. Ember, Dune, Rime... gently. Now give them your light.',
      '...Look. Look at it. The first flame of the Aether Lantern, burning again. Every rift on the isles just flinched.',
      'It is not enough. Four shards are still out there, and the Tyrant holds the last. But tonight the Lantern Watch remembers what it is for.',
    ],
    rewards: mainRewards(29, 0.35, { card: 'c_aldara', bond: 40, flags: ['lantern_first_flame'] }),
  }),
  main({
    id: 'mq_045',
    name: 'Call of the Hollow Throne',
    level: 30,
    zone: 'havenreach',
    giver: 'high_warden_aldara',
    turnIn: 'rift_scholar_elowen',
    requires: ['mq_044'],
    steps: [
      step(undefined, talk('rift_scholar_elowen', "Hear Elowen's reading of the Lantern flame")),
    ],
    offer: [
      'The flame points the way to the other four shards. Elowen can read it; I cannot.',
      'Go to her. Whatever she asks of you next, the Watch will stand behind it.',
    ],
    complete: [
      "The flame points four ways at once! Four shards, four roads. I've mapped them all.",
      'One burns deep in the rifts themselves: we can delve for it. One was swallowed by the Colossi, great beasts drawn to shard-light: we must hunt them.',
      'One lies in the Sunken Reliquary at the foot of the Drowned Stair, where the masked ones carried everything they bought.',
      "And the last is set in a crown on the Hollow Throne. Vhal'Zeroth wears it. The rift to his throne stands open; the Warlord Siege can begin whenever you are ready.",
      'No pressure.',
    ],
    rewards: mainRewards(30, 0.3, {
      gold: 30,
      gear: 'epic',
      items: items(['skill_potion', 1], ['book_precision', 1], ['rift_energy_tonic', 1]),
      flags: [
        'story_complete',
        'unlock_rift',
        'unlock_colossus',
        'unlock_deepvault',
        'unlock_siege',
      ],
    }),
  }),
];

/**
 * The endgame roads to the four remaining shards (world quests): Rift Delve,
 * the Colossus Hunt, the Deepvault (the Drowned Stair) and the Warlord Siege.
 */
export const WORLD_QUESTS: QuestDef[] = [
  world({
    id: 'wq_001',
    name: 'The Rift Shard',
    level: 30,
    zone: 'rift_1',
    giver: 'rift_scholar_elowen',
    turnIn: 'rift_scholar_elowen',
    requires: ['mq_045'],
    steps: [step(undefined, arrive('rift_1', 'Enter the Rift Delve (ask Elowen for instances)'))],
    offer: [
      'The rifts are wounds where the Hollow presses against the isles. Inside, shard-light pools like blood in a cut.',
      'I can open a way into the Rift Delve from here. Go in, see it for yourself, and come back in one piece. Ideally one piece.',
    ],
    complete: [
      "You're back, and not noticeably on fire! Every run into the rifts brings back shard-dust we can temper with.",
      'The Rift Shard itself sits deeper than we can reach yet. Keep delving. Each time you go down, the rifts get a little quieter.',
    ],
    rewards: {
      xp: 0,
      silver: 1500,
      card: 'c_elowen',
      items: items(['rift_energy_tonic', 1], ['aether_shard', 400]),
    },
  }),
  world({
    id: 'wq_002',
    name: 'Hunt of the Colossi',
    level: 30,
    zone: 'colossus_cinderhide',
    giver: 'rift_scholar_elowen',
    turnIn: 'rift_scholar_elowen',
    requires: ['mq_045'],
    steps: [
      step(undefined, kill('cinderhide', 1, 'Defeat the Cinderhide Behemoth (Colossus Hunt)')),
    ],
    offer: [
      'Four Colossi roam the deep places of the isles, and each carries a sliver of the Titan Shard in its heart.',
      "Start with the Cinderhide Behemoth. It's slow, it's armoured and it breathes fire, which I'm told is manageable.",
    ],
    complete: [
      "The first sliver! It's warm. Everything about that beast was warm.",
      "Three more Colossi carry the rest: Gloomfang, the Rimebound Wyrm and the Dunestrider. Whenever you're ready.",
    ],
    rewards: { xp: 0, silver: 2000, items: items(['colossus_mark', 10], ['leap_crystal', 6]) },
  }),
  world({
    id: 'wq_003',
    name: 'The Drowned Stair',
    level: 30,
    zone: 'deepvault_1',
    giver: 'rift_scholar_elowen',
    turnIn: 'high_warden_aldara',
    requires: ['mq_045'],
    steps: [
      step(
        undefined,
        kill('drowned_warden', 1, 'Defeat the Drowned Warden in the Sunken Reliquary'),
      ),
      step(
        'The reliquary floods deeper. Somewhere below, a voice is singing.',
        kill('tide_siren', 1, 'Silence the Tide Siren'),
      ),
    ],
    offer: [
      "The masked ones' letters all named the Drowned Stair. I found it: a sea-cave that leads down to a sunken reliquary.",
      "Everything they bought, they carried down there. Including, I'm fairly sure, the Tide Shard. Two guardians stand in the way.",
    ],
    complete: [
      "The Tide Shard. And the masked ones' ledgers, soaked but legible. We will know every name they bought.",
      'You have done more for this city in a season than the Watch did in a generation. Rest. Then, the throne.',
    ],
    rewards: { xp: 0, silver: 3000, gold: 100, items: items(['vault_token', 3]) },
  }),
  world({
    id: 'wq_004',
    name: 'The Hollow Throne',
    level: 30,
    zone: 'siege_2',
    giver: 'high_warden_aldara',
    turnIn: 'high_warden_aldara',
    requires: ['mq_045'],
    steps: [
      step(
        undefined,
        kill('hellhound_kharos', 1, 'Defeat Kharos at the gate of the Hollow Throne'),
        kill('hellhound_vesh', 1, 'Defeat Vesh at the gate of the Hollow Throne'),
      ),
      step(
        'Beyond the gate, on a throne of black glass, the Tyrant waits.',
        kill('vhalzeroth', 1, "Defeat Vhal'Zeroth, the Hollow Tyrant"),
      ),
      step(
        'The Tyrant falls, and something of him refuses to.',
        kill('vhalzeroth_phantom', 1, "Banish the Phantom of Vhal'Zeroth"),
      ),
    ],
    offer: [
      "The Warlord Siege. Beyond the rift waits the Hollow Throne, and on it Vhal'Zeroth, wearing the Crown Shard.",
      'His hounds, Kharos and Vesh, guard the gate. Then the Tyrant himself. They say that when he falls, something of him refuses to.',
      'Gather the strongest blades you know. Bring the Crown Shard home, and the Lantern will burn whole again.',
    ],
    complete: [
      'The Crown Shard. I never thought I would hold it.',
      'The Lantern burns over Havenreach brighter than it has in three hundred years, and the Hollow is quiet beneath us.',
      "The Watch swore a new oath tonight: 'While one bearer stands, the dark below shall wait.' We had to change the words. They are about you.",
    ],
    rewards: {
      xp: 0,
      silver: 5000,
      gold: 500,
      items: items(['warlord_relic', 5], ['skill_potion', 1]),
      flags: ['lantern_relit'],
    },
  }),
];
